import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Crosshair, Download, Printer, Trophy, UsersRound } from "lucide-react";
import { Loading } from "./ui";
import { PrintFooter, PrintHeader } from "./PrintReport";
import { useApi } from "../api/client";
import { usePortalLink } from "../utils/portalLink";
import { rowClickNewTab } from "../utils/rowClick";
import { downloadCsv } from "../utils/download";
import { formatScoreCentres } from "../utils/score";
import { ResultsListEntry } from "../data/types";

/*
 * The published results list, in the form the sport actually reads them.
 *
 * Every trophy and every match is one collapsible row; opening it shows the
 * placings grouped by class AND grade — "Target Rifle - A" and "Target Rifle -
 * B" as separate boards, each starting at place 1. That grouping is the point:
 * grades exist so a B-grade shooter is not placed against an A-grade one.
 *
 * Trophies lead, matches follow, because a competitor looks for their trophy
 * placing first. A match row also prints the shot-by-shot string — "V5VVVVVVV"
 * — which is how a scorer checks a card against the board.
 *
 * This sits alongside the existing aggregates view rather than replacing it:
 * that one answers "who won", this one is the full record.
 */
export default function ResultsList({ competitionId }: { competitionId: string }) {
  const link = usePortalLink();
  const { data, loading } = useApi<{ competition?: string; teamEvent?: boolean; entries: ResultsListEntry[] } | null>(
    competitionId ? `/competitions/${competitionId}/results-list` : null,
    null
  );
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [openTeams, setOpenTeams] = useState<Record<string, boolean>>({});
  /* On paper every team is opened: a printed sheet cannot be clicked. */
  const [printing, setPrinting] = useState(false);
  // Memoised: a fresh array each render would re-run the print listener effect.
  const entries = useMemo(() => data?.entries ?? [], [data]);

  /*
   * Printing has to open everything first: a collapsed panel is not in the DOM
   * at all, so a report printed as the page stands would be a list of headings.
   * The same applies to Ctrl+P, hence the beforeprint listener rather than only
   * a handler on the button.
   */
  useEffect(() => {
    const onBefore = () => {
      setOpen(Object.fromEntries(entries.map((e) => [e.id, true])));
      setPrinting(true);
    };
    const onAfter = () => setPrinting(false);
    window.addEventListener("beforeprint", onBefore);
    window.addEventListener("afterprint", onAfter);
    return () => {
      window.removeEventListener("beforeprint", onBefore);
      window.removeEventListener("afterprint", onAfter);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries]);

  const printReport = () => {
    setOpen(Object.fromEntries(entries.map((e) => [e.id, true])));
    setPrinting(true);
    // A moment for React to commit the expansion before the dialog opens.
    setTimeout(() => window.print(), 60);
  };

  if (loading) return <div className="card"><Loading /></div>;
  if (!entries.length) {
    return (
      <div className="card p-10 text-center text-sm text-muted">
        No results yet — they appear here once a shoot has been imported.
      </div>
    );
  }

  const toggle = (id: string) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const toggleTeam = (key: string) => setOpenTeams((o) => ({ ...o, [key]: !o[key] }));

  const exportEntry = (e: ResultsListEntry) => {
    const extra = e.kind === "aggregate" ? "Matches" : "Shots";
    return downloadCsv(
      `${e.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`,
      ["Class", "Grade", "Place", "Name", "Club", "State", extra, "Score", "Centres"],
      e.groups.flatMap((g) =>
        g.rows.map((r) => [
          g.rifleClass,
          g.grade,
          r.place,
          r.shooter,
          r.club,
          r.state,
          e.kind === "aggregate" ? r.matchesCounted ?? "" : r.shotString ?? "",
          r.score,
          r.xCount,
        ])
      )
    );
  };

  return (
    <div className="space-y-2 print-full">
      <PrintHeader
        title={data?.competition || "Results"}
        subtitle="Official Results"
        meta={[String(entries.length) + " sections", String(entries.filter((x) => x.kind === "match").length) + " matches"]}
      />

      <div className="no-print flex justify-end">
        <button
          onClick={printReport}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-line bg-white px-3 text-[13px] font-semibold text-ink transition-colors hover:bg-slate-50"
        >
          <Printer size={15} /> Print / PDF
        </button>
      </div>

      {entries.map((e) => {
        const isOpen = !!open[e.id];
        return (
          <div key={e.id} className="card overflow-hidden">
            <button
              onClick={() => toggle(e.id)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
            >
              <ChevronDown size={16} className={`shrink-0 text-muted transition-transform ${isOpen ? "" : "-rotate-90"}`} />
              {e.kind === "aggregate" ? (
                <Trophy size={16} className="shrink-0 text-warning" />
              ) : (
                <Crosshair size={16} className="shrink-0 text-primary" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-bold text-ink">
                  {e.name}
                  {e.kind === "aggregate" && (
                    <span className="ml-2 rounded-md bg-warning-soft px-1.5 py-0.5 text-[11px] font-bold uppercase text-warning">
                      {/* A competition is what is actually won at the meeting;
                          the rest are aggregates over it. */}
                      {e.championshipId ? "Competition" : "Aggregate"}
                    </span>
                  )}
                </span>
                {e.subtitle && <span className="block truncate text-[13px] text-muted">{e.subtitle}</span>}
              </span>
              <span className="flex shrink-0 items-center gap-3 text-[13px] text-muted">
                {/* Says up front that a team board is inside. It sits below the
                    class boards, so on a large field it is well off the screen
                    and was being missed entirely. */}
                {!!e.teams?.length && (
                  <span className="hidden items-center gap-1 rounded-md bg-primary-soft px-1.5 py-0.5 font-semibold text-primary sm:inline-flex">
                    <UsersRound size={12} /> {e.teams.length} teams
                  </span>
                )}
                <span>
                  {e.competitors} {e.competitors === 1 ? "entry" : "entries"}
                </span>
              </span>
            </button>

            {isOpen && (
              <div className="border-t border-line">
                {!e.groups.length && (
                  <p className="px-4 py-6 text-center text-[13px] text-muted">No results recorded for this yet.</p>
                )}
                {e.groups.map((g) => (
                  <div key={g.label}>
                    {/* One board per class and grade, each starting at place 1. */}
                    <div className="border-b border-line bg-slate-50 px-4 py-1.5 text-[13px] font-extrabold text-ink">
                      {g.label}
                    </div>
                    <div className="overflow-x-auto">
                      {/*
                        Fixed columns so every class board on the sheet lines up.
                        Sized to their contents, each board set its own widths —
                        "HRSLRC" in one and "KRC" in the next moved Club, State
                        and Score to different places down the page.
                      */}
                      <table className="w-full min-w-[720px] table-fixed">
                        <thead>
                          <tr className="border-b border-line">
                            <th className="th w-[7%]">Place</th>
                            <th className="th w-[24%]">Name</th>
                            <th className="th w-[11%]">Club</th>
                            <th className="th w-[9%]">State</th>
                            {/*
                              An aggregate row is about how many matches were
                              counted; a match row is about the shots fired and
                              the target they went into.
                            */}
                            {e.kind === "aggregate" && <th className="th w-[27%] text-center">Matches</th>}
                            {e.kind === "match" && <th className="th w-[27%]">Record Shots</th>}
                            <th className="th w-[13%] text-right">Score</th>
                            {e.kind === "match" && <th className="th no-print w-[9%] text-center">Target</th>}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-line">
                          {g.rows.map((r, i) => (
                            <tr
                              key={`${r.sid}-${r.place}`}
                              onClick={rowClickNewTab(link(`/shooters/${r.sid}`))}
                              className={`cursor-pointer transition-colors hover:bg-primary-soft/40 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}
                            >
                              <td className={`td font-bold ${r.place <= 3 ? "text-accent" : ""}`}>{r.place}</td>
                              <td className="td">
                                <Link to={link(`/shooters/${r.sid}`)} target="_blank" rel="noopener noreferrer" className="font-bold text-ink hover:text-primary">
                                  {r.shooter}
                                </Link>
                              </td>
                              <td className="td truncate text-slate-600" title={r.club}>{r.club}</td>
                              <td className="td text-muted">{r.state || "—"}</td>
                              {e.kind === "aggregate" && (
                                <td className="td text-center text-slate-600">{r.matchesCounted ?? "—"}</td>
                              )}
                              {e.kind === "match" && (
                                <td className="td break-all font-mono text-[12px] tracking-wide text-slate-600">{r.shotString || "—"}</td>
                              )}
                              <td className="td text-right font-bold">
                                {formatScoreCentres(r.score, r.xCount, r.centreLabel)}
                              </td>
                              {e.kind === "match" && (
                                <td className="td no-print text-center">
                                  {r.stringIds?.length ? (
                                    /*
                                     * One link per string, because a competitor
                                     * may shoot the range more than once. A long
                                     * list is capped so a data problem in the
                                     * upload cannot swamp the row.
                                     */
                                    <span className="inline-flex items-center justify-center gap-1">
                                      {r.stringIds.slice(0, 3).map((sid) => (
                                        <Link
                                          key={sid}
                                          to={link(`/strings/${sid}`)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          title="View the shots on the target"
                                          className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-line text-primary transition-colors hover:bg-primary-soft"
                                        >
                                          <Crosshair size={13} />
                                        </Link>
                                      ))}
                                      {r.stringIds.length > 3 && (
                                        <span className="text-[12px] text-muted">+{r.stringIds.length - 3}</span>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
                {/*
                  Team standings, after the individual boards — the order the
                  NRAA prints them. One board, not split by class: a team is a
                  club or state side of mixed classes, and its total is every
                  counting score its members put up.
                */}
                {!!e.teams?.length && (
                  <div>
                    <div className="flex items-center gap-2 border-b border-t border-line bg-slate-50 px-4 py-1.5 text-[13px] font-extrabold text-ink">
                      <UsersRound size={14} className="text-primary" /> Team Standings
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[560px]">
                        <thead>
                          <tr className="border-b border-line">
                            <th className="th w-14">Place</th>
                            <th className="th">Team</th>
                            <th className="th text-center">Members Counting</th>
                            <th className="th text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-line">
                          {e.teams.map((t, i) => {
                            const teamKey = `${e.id}|${t.team}`;
                            const teamOpen = !!openTeams[teamKey];
                            return (
                              <React.Fragment key={t.team}>
                                <tr
                                  onClick={() => toggleTeam(teamKey)}
                                  className={`cursor-pointer transition-colors hover:bg-primary-soft/30 ${
                                    i % 2 === 1 ? "bg-slate-50/50" : ""
                                  }`}
                                >
                                  <td className={`td font-bold ${t.place <= 3 ? "text-accent" : ""}`}>{t.place}</td>
                                  <td className="td font-bold text-ink">
                                    <span className="flex items-center gap-1.5">
                                      {/* A team total is a sum of other people's
                                          scores, so it has to be openable. */}
                                      <ChevronDown
                                        size={13}
                                        className={`no-print shrink-0 text-muted transition-transform ${teamOpen ? "" : "-rotate-90"}`}
                                      />
                                      {t.team}
                                    </span>
                                  </td>
                                  <td className="td text-center text-slate-600">{t.members}</td>
                                  <td className="td text-right font-bold">{formatScoreCentres(t.score, t.xCount)}</td>
                                </tr>

                                {(teamOpen || printing) && !!t.entries?.length && (
                                  <tr>
                                    <td colSpan={4} className="bg-slate-50/70 px-4 py-2">
                                      <table className="w-full">
                                        <thead>
                                          <tr className="border-b border-line">
                                            <th className="th">Counting for {t.team}</th>
                                            <th className="th">Club</th>
                                            <th className="th">Class</th>
                                            {e.kind === "aggregate" && <th className="th text-center">Matches</th>}
                                            <th className="th text-right">Score</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-line">
                                          {t.entries.map((m) => (
                                            <tr
                                              key={`${m.sid}-${m.rifleClass}`}
                                              onClick={rowClickNewTab(link(`/shooters/${m.sid}`))}
                                              className="cursor-pointer transition-colors hover:bg-primary-soft/40"
                                            >
                                              <td className="td">
                                                <Link
                                                  to={link(`/shooters/${m.sid}`)}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="font-bold text-ink hover:text-primary"
                                                >
                                                  {m.shooter}
                                                </Link>
                                              </td>
                                              <td className="td text-slate-600">{m.club}</td>
                                              <td className="td text-muted">
                                                {m.rifleClass}
                                                {m.grade ? ` · ${m.grade}` : ""}
                                              </td>
                                              {e.kind === "aggregate" && (
                                                <td className="td text-center text-slate-600">{m.matches}</td>
                                              )}
                                              <td className="td text-right font-bold">
                                                {formatScoreCentres(m.score, m.xCount, m.centreLabel)}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <div className="flex justify-end border-t border-line px-4 py-2">
                  <button
                    onClick={() => exportEntry(e)}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-line bg-white px-3 text-[13px] font-semibold text-ink transition-colors hover:bg-slate-50"
                  >
                    <Download size={13} /> Export CSV
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <PrintFooter />
    </div>
  );
}
